// Patent claims exercised: P-004 Claims 4-5,11-13 (provider portal, accountless access)
import { API_BASE } from "./config";
import React, { useState } from 'react';

function ProviderPortal() {
  const [step, setStep] = useState('passcode'); // 'passcode', 'form', 'success'
  const [passcode, setPasscode] = useState('');
  const [reportInfo, setReportInfo] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [responseText, setResponseText] = useState('');
  const [responseCategory, setResponseCategory] = useState('general');
  const [isUrgent, setIsUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  // Auto-fill passcode from URL parameter
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlPasscode = urlParams.get('passcode');
    if (urlPasscode) {
      setPasscode(urlPasscode);
    }
    
    // Load response categories
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/provider-portal/response-categories`);
      const data = await response.json();
      setCategories(data.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleValidatePasscode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/provider-portal/validate-passcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: passcode.trim() })
      });

      const data = await response.json();

      if (data.valid) {
        setReportInfo(data);
        
        // Load providers for this report
        const providersResponse = await fetch(
          `${API_BASE}/api/provider-portal/report/${data.report_id}/providers?passcode=${passcode.trim()}`
        );
        const providersData = await providersResponse.json();
        setProviders(providersData.providers);
        
        setStep('form');
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to validate passcode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    
    if (!selectedProvider) {
      setError('Please select your name from the provider list');
      return;
    }
    
    if (!responseText.trim()) {
      setError('Please enter your response');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/provider-portal/submit-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passcode: passcode.trim(),
          provider_id: parseInt(selectedProvider),
          response_text: responseText,
          response_category: responseCategory,
          is_urgent: isUrgent
        })
      });

      if (response.ok) {
        setStep('success');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit response');
      }
    } catch (err) {
      setError('Failed to submit response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // PASSCODE ENTRY STEP
  if (step === 'passcode') {
    return (
      <div className="pp-pageWrapper">
        <div className="pp-cardWrapper">
          <div className="pp-cardHeader">
            <h1 className="pp-portalTitle">
              Healist Provider Portal
            </h1>
            <p className="pp-subtitle">
              Enter your one-time access code to view patient report
            </p>
          </div>

          <form onSubmit={handleValidatePasscode}>
            {error && (
              <div className="pp-errorMessage">
                {error}
              </div>
            )}

            <div className="pp-formFieldGroup">
              <label className="pp-accessCodeLabel">
                Access Code
              </label>
              <input
                type="text"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Enter 12-character code"
                required
                className="pp-passcodeInput"
              />
              <p className="pp-helperText">
                This code was provided in your email
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !passcode.trim()}
              className="pp-validateBtn" style={{ cursor: loading || !passcode.trim() ? 'not-allowed' : 'pointer', opacity: loading || !passcode.trim() ? 0.6 : 1 }}
            >
              {loading ? 'Validating...' : 'Provide Comments'}
            </button>
          </form>

          <div className="pp-importantNotesSection">
            <strong>Important:</strong>
            <ul className="pp-importantPointsList">
              <li>Access codes can only be used once</li>
              <li>The patient PDF was attached to your email</li>
              <li>This portal is for submitting your response only</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // RESPONSE FORM STEP
  if (step === 'form') {
    return (
      <div className="pp-pageWrapperForm">
        <div className="pp-cardWrapperForm">
          <div className="pp-formHeader">
            <h2 className="pp-patientNameHeading">
              Patient: {reportInfo.patient_name}
            </h2>
            <p className="pp-reportDateText">
              Report Date: {reportInfo.report_date}
            </p>
          </div>

          <form onSubmit={handleSubmitResponse}>
            {error && (
              <div className="pp-errorMessageForm">
                {error}
              </div>
            )}

            {/* Provider Selection */}
            <div className="pp-providerSelectionField">
              <label className="pp-providerSelectionLabel">
                Select Your Name *
              </label>
              <select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                required
                className="pp-providerSelect"
              >
                <option value="">-- Select Provider --</option>
                {providers.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.provider_name} ({provider.provider_type})
                  </option>
                ))}
              </select>
            </div>

            {/* Response Category */}
            <div className="pp-responseCategoryField">
              <label className="pp-categoryLabel">
                Response Category
              </label>
              <select
                value={responseCategory}
                onChange={(e) => setResponseCategory(e.target.value)}
                className="pp-categorySelect"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Response Text */}
            <div className="pp-responseTextField">
              <label className="pp-responseLabel">
                Your Response / Recommendations *
              </label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                required
                rows={8}
                placeholder="Enter your feedback, recommendations, or comments for the patient..."
                className="pp-responseTextarea"
              />
            </div>

            {/* Urgent Checkbox */}
            <div className="pp-urgentCheckboxField">
              <label className="pp-urgentCheckboxLabel">
                <input
                  type="checkbox"
                  checked={isUrgent}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                  className="pp-urgentCheckbox"
                />
                <span className="pp-urgentCheckboxText">
                  🚨 Mark as Urgent (requires immediate patient attention)
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="pp-submitBtn" style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Submitting...' : 'Submit Response'}
            </button>
          </form>

          <div className="pp-noteSection">
            <strong>Note:</strong> Your response will be securely transmitted to the patient. 
            They will receive an email notification and can view your full response in their Healist account.
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS STEP
  if (step === 'success') {
    return (
      <div className="pp-pageWrapperSuccess">
        <div className="pp-cardWrapperSuccess">
          <div className="pp-successIconPlaceholder">
          </div>
          
          <h2 className="pp-successHeading">
            Response Submitted Successfully!
          </h2>
          
          <p className="pp-successMessage">
            Your feedback has been securely transmitted to {reportInfo.patient_name}. 
            The patient will receive an email notification and can view your response in their account.
          </p>

          {isUrgent && (
            <div className="pp-urgentNotificationSection">
              <p className="pp-urgentNotificationText">
                🚨 Urgent Response Notification Sent
              </p>
            </div>
          )}

          <div className="pp-finalNoteSection">
            <p className="pp-finalNoteText">
              <strong>Note:</strong> This access code has been used and is no longer valid. 
              You may now close this window.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default ProviderPortal;